import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import API_URL from '../config/api';
import { 
  FiUser, 
  FiBriefcase, 
  FiMapPin, 
  FiCalendar, 
  FiKey,
  FiMail,
  FiArrowLeft,
  FiSave,
  FiZap,
  FiCheckCircle,
  FiClock
} from 'react-icons/fi';

const EditClient = () => {
  const { clientNumber } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    fullName: '',
    email: '',
    company: '',
    username: '',
    clientPassword: '',
    address: '',
    startDate: '',
    dueDate: '',
    twoDigitCode: '',
    zidCode: '',
    powerStatus: 'activo',
    urgent: false,
    cancellationNumber: ''
  });

  useEffect(() => {
    fetchClient();
  }, [clientNumber]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients/${clientNumber}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const client = response.data.client;
      setFormData({
        firstName: client.firstName || '',
        lastName: client.lastName || '',
        fullName: client.fullName || '',
        email: client.email || '',
        company: client.company || '',
        username: client.username || '',
        clientPassword: '',
        address: client.address || '',
        startDate: client.startDate ? client.startDate.split('T')[0] : '',
        dueDate: client.dueDate ? client.dueDate.split('T')[0] : '',
        twoDigitCode: client.twoDigitCode || '',
        zidCode: client.zidCode || '',
        powerStatus: client.powerStatus || 'activo',
        urgent: client.urgent || false,
        cancellationNumber: client.cancellationNumber || ''
      });
    } catch (error) {
      console.error('Error fetching client:', error);
      toast.error('Error al cargar el cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(
        `${API_URL}/api/clients/${clientNumber}`,
        formData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Cliente actualizado correctamente');
      navigate(`/clients/${clientNumber}`);
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error('Error al actualizar cliente');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button
        onClick={() => navigate(`/clients/${clientNumber}`)}
        className="flex items-center text-blue-600 hover:text-blue-800 mb-6"
      >
        <FiArrowLeft className="mr-2" /> Volver
      </button>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
          <h1 className="text-2xl font-bold">Editar Cliente #{clientNumber}</h1>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nombre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiUser className="inline mr-2" /> Nombre
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
                required
              />
            </div>

            {/* Apellido */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiUser className="inline mr-2" /> Apellido
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiMail className="inline mr-2" /> Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>

            {/* Compañía */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiBriefcase className="inline mr-2" /> Compañía
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>

            {/* Dirección */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiMapPin className="inline mr-2" /> Dirección
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
                required
              />
            </div>

            {/* Usuario */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiKey className="inline mr-2" /> Usuario
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>

            {/* Contraseña (opcional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiKey className="inline mr-2" /> Nueva Contraseña
              </label>
              <input
                type="password"
                name="clientPassword"
                value={formData.clientPassword}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="Dejar vacío para no cambiar"
              />
            </div>

            {/* Fecha inicio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiCalendar className="inline mr-2" /> Fecha inicio
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
                required
              />
            </div>

            {/* Fecha vencimiento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiCalendar className="inline mr-2" /> Fecha vencimiento
              </label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
                required
              />
            </div>

            {/* Código 2D */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiKey className="inline mr-2" /> Código 2D
              </label>
              <input
                type="number"
                name="twoDigitCode"
                value={formData.twoDigitCode}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
                min="0"
                max="99"
              />
            </div>

            {/* ZID Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiKey className="inline mr-2" /> ZID Code
              </label>
              <input
                type="text"
                name="zidCode"
                value={formData.zidCode}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>

            {/* Account Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiKey className="inline mr-2" /> Account Number
              </label>
              <input
                type="text"
                name="cancellationNumber"
                value={formData.cancellationNumber}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>

            {/* Estado de luz */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <FiZap className="inline mr-2" /> Estado de la Luz
              </label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="powerStatus"
                    value="activo"
                    checked={formData.powerStatus === 'activo'}
                    onChange={handleChange}
                  />
                  <FiCheckCircle className="text-green-500" />
                  <span>Activa</span>
                </label>
                <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="powerStatus"
                    value="espera"
                    checked={formData.powerStatus === 'espera'}
                    onChange={handleChange}
                  />
                  <FiClock className="text-yellow-500" />
                  <span>En Espera</span>
                </label>
                <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="powerStatus"
                    value="desconectado"
                    checked={formData.powerStatus === 'desconectado'}
                    onChange={handleChange}
                  />
                  <FiZap className="text-red-500" />
                  <span>Desconectada</span>
                </label>
              </div>
            </div>

            {/* Urgente */}
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="urgent"
                  checked={formData.urgent}
                  onChange={handleChange}
                  className="w-4 h-4 text-red-600"
                />
                <span className="text-sm font-medium text-gray-700">Marcar como URGENTE</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate(`/clients/${clientNumber}`)}
              className="px-6 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <FiSave />
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditClient;